// Les crochets qui relient React à la couche de données.
//
// `store.js` expose partout la même forme : `subscribe(cb)` rend une fonction
// d'arrêt. C'est exactement ce que React attend d'un effet. Ce fichier fait la
// jonction une fois pour toutes, plutôt que de la refaire dans chaque écran.

import { useEffect, useRef, useState } from "react";
import Store from "../store.js";

/**
 * Suit un abonnement de `store.js`.
 *
 * @param {Function} abonner   (cb) => fonction d'arrêt. DOIT être stable :
 *                             passez-la via useCallback si elle dépend de quelque chose.
 * @param {*} initiale         ce qu'on affiche avant la première émission
 * @param {Array} deps         de quoi dépend l'abonnement
 */
export function useAbonnement(abonner, initiale, deps = []) {
  const [valeur, setValeur] = useState(initiale);

  useEffect(() => {
    let vivant = true;
    const stop = abonner((v) => {
      // Un abonnement peut émettre après le démontage — Firestore n'a aucune
      // raison de savoir que l'écran a changé. Sans ce garde-fou, React
      // avertirait à chaque navigation.
      if (vivant) setValeur(v);
    });
    return () => {
      vivant = false;
      stop && stop();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return valeur;
}

/* `useCertifie` vivait ici — plus aucun écran ne l'appelait : le sceau se lit
   par `useMembre(...).certifie` (et `useCertifiePage` pour une page, plus
   bas, qui sert toujours). Retirée le 20 octobre 2026 (audit) — archive :
   ~/Desktop/koinonia-archive-code-retire-2026-10-20.zip. */

/**
 * Le nom, la photo et le badge d'un membre — tels qu'ils sont MAINTENANT.
 *
 * Une publication recopie le nom et la photo de son auteur au moment où elle
 * est écrite. C'est commode — aucune lecture en plus pour l'afficher — mais
 * c'est un instantané : le jour où le membre ajoute enfin sa photo de profil,
 * tout ce qu'il a publié avant reste sans visage. Et s'il corrige son nom, ses
 * anciennes publications gardent l'ancien.
 *
 * On demande donc au profil, et l'instantané ne sert plus que de secours le
 * temps de la réponse — pas de case vide qui clignote. Une fois le vrai profil
 * connu, c'est lui qui fait foi, y compris pour dire « pas de photo ».
 *
 * Le coût est faible : `ctx.profil` garde un cache par session, donc une
 * lecture par membre et par session, quel que soit le nombre de publications.
 */
export function useMembre(ctx, uid, secours) {
  const [membre, setMembre] = useState(null);

  useEffect(() => {
    setMembre(null);
    if (!uid || !ctx?.profil) return;
    let vivant = true;
    ctx.profil(uid).then((u) => vivant && u && setMembre(u));
    return () => {
      vivant = false;
    };
  }, [uid, ctx]);

  if (membre)
    return { nom: membre.nom || "Membre", photo: membre.photo || "", certifie: Boolean(membre.certifie) };
  return { nom: secours?.nom || "Membre", photo: secours?.photo || "", certifie: false };
}

/**
 * La même chose pour une page.
 *
 * `ctx.profil` ne connaît que les membres : on tient donc ici un petit cache de
 * pages, à la même échelle — une lecture par page et par session.
 */
const pagesConnues = new Map();

export function useCertifiePage(pageId) {
  const [certifie, setCertifie] = useState(false);

  useEffect(() => {
    if (!pageId) return;
    let vivant = true;
    if (!pagesConnues.has(pageId))
      pagesConnues.set(pageId, Store.pages.get(pageId).catch(() => null));
    pagesConnues.get(pageId).then((pg) => vivant && setCertifie(Boolean(pg?.certifie)));
    return () => {
      vivant = false;
    };
  }, [pageId]);

  return certifie;
}

/**
 * Les publications que j'ai soutenues d'un Amen.
 *
 * Comme les statuts, l'application ne l'écoute qu'une fois dans `app.js` et le
 * redistribue par `ctx.onAmens` : c'est ce qui permet à un Amen donné dans le
 * fil d'apparaître aussitôt sur la même publication vue ailleurs.
 */
export function useAmens(ctx) {
  const [amens, setAmens] = useState(ctx.amens || new Map());
  useEffect(() => ctx.onAmens?.(setAmens), [ctx]);
  return amens;
}

/**
 * Qui a un statut en cours — pour cercler son avatar de bleu.
 *
 * L'application n'écoute les statuts qu'UNE fois, dans `app.js`, et les
 * redistribue par `ctx.onStatuts`. Chaque écran qui s'abonnerait lui-même
 * paierait les mêmes lectures une seconde fois.
 */
export function useStatuts(ctx) {
  const [avecStatut, setAvecStatut] = useState(ctx.avecStatut || new Set());
  useEffect(() => ctx.onStatuts?.(setAvecStatut), [ctx]);
  return avecStatut;
}

/**
 * Redessine quand les signaux changent.
 *
 * Les signaux (`js/signaux.js`) sont ce que l'application a appris du membre :
 * son voisinage, ce qu'il a déjà vu, qui il ne veut plus qu'on lui propose. Ils
 * arrivent APRÈS l'écran — le voisinage demande plusieurs lectures — et les
 * suggestions doivent alors se refaire toutes seules.
 *
 * Rend un simple compteur, à mettre en dépendance d'un `useMemo`.
 */
export function useSignaux(signaux) {
  const [version, setVersion] = useState(0);
  useEffect(() => {
    if (!signaux) return;
    return signaux.surChangement(() => setVersion((n) => n + 1));
  }, [signaux]);
  return version;
}

/**
 * Exécute une action une seule fois, un délai après le montage.
 *
 * Sert à « ouvrir la page vaut lecture » : on ne marque pas les notifications
 * comme lues à l'instant même où elles s'affichent, sinon la pastille
 * disparaîtrait avant que l'œil ait eu le temps de la voir.
 */
export function useApresUnInstant(action, delai = 1200) {
  const fait = useRef(false);
  useEffect(() => {
    if (fait.current) return;
    fait.current = true;
    const t = setTimeout(action, delai);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
